import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Coins, ExternalLink, Loader2, UserMinus, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { AVATAR_CATEGORIES, avatarSrc } from "../../lib/avatars";
import { TROPHY_SRC, crestFor, crestSrc } from "../../lib/rewards";
import { removeFriend, sendFriendRequest } from "../../lib/friends";

export const NOTE_COLORS = { none: "transparent", red: "#ef4444", orange: "#f97316", yellow: "#eab308", green: "#22c55e", blue: "#3b82f6", purple: "#a855f7" };
const fallbackAvatar = (name) => {
  const people = [...AVATAR_CATEGORIES[0].items, ...AVATAR_CATEGORIES[1].items];
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return people[h % people.length].url;
};
const pct = (v) => (v == null ? "—" : `${v}%`);
const STREET_LABEL = { PREFLOP: "Pre-flop", FLOP: "Flop", TURN: "Turn", RIVER: "River" };

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg bg-black/40 border border-white/10 px-2 py-1.5 text-center" data-testid={testId}>
    <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
    <div className="font-mono text-base text-amber-100 leading-tight">{value}</div>
  </div>
);

const StatsTab = ({ s }) => (
  <div className="flex flex-col gap-3" data-testid="player-card-stats">
    <div className="grid grid-cols-4 gap-1.5">
      <Stat label="VPIP" value={pct(s.vpip)} testId="stat-vpip" />
      <Stat label="PFR" value={pct(s.pfr)} testId="stat-pfr" />
      <Stat label="WTSD" value={pct(s.wtsd)} testId="stat-wtsd" />
      <Stat label="Hands" value={s.hands.toLocaleString()} testId="stat-hands" />
    </div>
    <table className="w-full text-xs">
      <thead><tr className="text-[9px] uppercase tracking-[0.2em] text-slate-400"><th className="text-left font-normal pb-1">Street</th><th className="font-normal pb-1">Raise %</th><th className="font-normal pb-1">Fold %</th></tr></thead>
      <tbody>
        {s.streets.map((r) => (
          <tr key={r.street} className="border-t border-white/5" data-testid={`stat-street-${r.street}`}>
            <td className="py-1 text-slate-300">{STREET_LABEL[r.street]}</td>
            <td className="py-1 text-center font-mono text-emerald-300">{pct(r.raise)}</td>
            <td className="py-1 text-center font-mono text-red-300">{pct(r.fold)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="grid grid-cols-2 gap-1.5 text-[11px]">
      <div className="rounded-lg bg-black/40 border border-white/10 px-3 py-1.5" data-testid="stat-cash"><span className="text-slate-400">Cash hands</span> <span className="font-mono text-amber-100 float-right">{s.cash_hands.toLocaleString()}</span></div>
      <div className="rounded-lg bg-black/40 border border-white/10 px-3 py-1.5" data-testid="stat-sng"><span className="text-slate-400">Sit N Go's</span> <span className="font-mono text-amber-100 float-right">{s.sng_played.toLocaleString()}{s.sng_wins ? <span className="text-emerald-300"> · {s.sng_wins} won</span> : null}</span></div>
    </div>
  </div>
);

const NotesTab = ({ userId, note, onSaved }) => {
  const [text, setText] = useState(note?.text || "");
  const [color, setColor] = useState(note?.color || "none");
  const [busy, setBusy] = useState(false);
  const dirty = text !== (note?.text || "") || color !== (note?.color || "none");
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(C.ApiRoutes.PLAYER_NOTE.replace("{user_id}", userId), { text, color });
      onSaved(data); toast.success("Note saved — only you can see it");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2" data-testid="player-card-notes">
      <div className="flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mr-1">Label</span>
        {Object.entries(NOTE_COLORS).map(([k, v]) => (
          <button key={k} data-testid={`note-color-${k}`} onClick={() => setColor(k)} title={k} aria-pressed={color === k}
            className={`w-5 h-5 rounded-full border-2 transition-transform ${color === k ? "scale-125 border-amber-300" : "border-white/20 hover:scale-110"}`}
            style={{ background: k === "none" ? "repeating-linear-gradient(45deg,#334155 0 3px,#0f172a 3px 6px)" : v }} />
        ))}
      </div>
      <textarea data-testid="note-text" value={text} onChange={(e) => setText(e.target.value.slice(0, 2000))} rows={4} placeholder="Folds to big bets… raises a lot in late position…"
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-amber-50 placeholder:text-slate-500 focus:border-amber-400/60 outline-none resize-none" />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-500">Private — only you can read your notes.</span>
        <button data-testid="note-save" onClick={save} disabled={!dirty || busy} className="btn-gold rounded-full px-4 py-1 text-xs font-display disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

const FriendButton = ({ card, setCard }) => {
  const [busy, setBusy] = useState(false);
  const uid = card.user.user_id;
  const run = (fn, next, msg) => async () => {
    setBusy(true);
    try { await fn(uid); setCard((c) => ({ ...c, friend: next })); if (msg) toast.success(msg); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const cls = "rounded-full px-3 py-1.5 text-xs font-display tracking-wider flex items-center gap-1.5 btn-lift disabled:opacity-50";
  if (card.friend === "friends") return (
    <div className="flex items-center gap-2">
      <span data-testid="friend-state" className={`${cls} bg-emerald-500/20 text-emerald-200 border border-emerald-400/30`}><Check className="w-3.5 h-3.5" /> FRIENDS</span>
      <button data-testid="friend-remove" onClick={run(removeFriend, "none", "Removed from your friends")} disabled={busy} title="Remove friend" className="p-1.5 rounded-full text-slate-400 hover:text-red-300 hover:bg-white/5"><UserMinus className="w-4 h-4" /></button>
    </div>
  );
  if (card.friend === "sent") return <button data-testid="friend-cancel" onClick={run(removeFriend, "none", "Request cancelled")} disabled={busy} className={`${cls} bg-white/5 text-amber-200 border border-amber-300/30`}>REQUEST SENT · cancel</button>;
  if (card.friend === "incoming") return (
    <div className="flex items-center gap-2">
      <button data-testid="friend-accept" onClick={run(sendFriendRequest, "friends", `You and ${card.user.display_name} are now friends`)} disabled={busy} className={`${cls} btn-gold`}><Check className="w-3.5 h-3.5" /> ACCEPT</button>
      <button data-testid="friend-decline" onClick={run(removeFriend, "none")} disabled={busy} className={`${cls} bg-white/5 text-slate-300`}>DECLINE</button>
    </div>
  );
  return <button data-testid="friend-add" onClick={run(sendFriendRequest, "sent", `Friend request sent to ${card.user.display_name}`)} disabled={busy} className={`${cls} btn-gold`}><UserPlus className="w-3.5 h-3.5" /> ADD FRIEND</button>;
};

// Player info card: opens top-left when you click another player's avatar at your table (server checks you share a table).
export const PlayerCard = ({ userId, tableId, isMe, onClose, onNoteSaved }) => {
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("stats");
  useEffect(() => {
    setCard(null); setErr(null);
    api.get(C.ApiRoutes.PLAYER_CARD.replace("{user_id}", userId), { params: { table_id: tableId } }).then((r) => setCard(r.data)).catch((e) => setErr(errorMessage(e)));
  }, [userId, tableId]);
  const u = card?.user;
  return (
    <aside data-testid="player-card" className="absolute left-[58px] top-[92px] z-40 w-[360px] gold-card rounded-2xl overflow-hidden card-slide" role="dialog" aria-label="Player info">
      <header className="flex items-center justify-between px-4 py-2 border-b border-amber-300/20 bg-black/40">
        <span className="text-[11px] uppercase tracking-[0.3em] gold-text">Player info</span>
        <button data-testid="player-card-close" onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-md bg-red-600 hover:bg-red-500 text-white flex items-center justify-center btn-lift"><X className="w-4 h-4" /></button>
      </header>
      {err && <div data-testid="player-card-error" className="px-4 py-6 text-sm text-red-300">{err === "not same table" ? "You need to be seated at this table to view other players." : err}</div>}
      {!card && !err && <div className="px-4 py-8 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {card && (
        <div className="p-4 flex flex-col gap-3">
          <div className="flex gap-4">
            <div className="relative shrink-0">
              <div className="w-[88px] h-[88px] rounded-xl overflow-hidden border-2 border-amber-300/70 shadow-[0_0_24px_rgba(251,191,36,0.3)] bg-slate-900">
                <img src={u.avatar ? avatarSrc(u.avatar) : fallbackAvatar(u.display_name)} alt={u.display_name} data-testid="player-card-avatar" className="w-full h-full object-cover" />
              </div>
              <div data-testid="player-card-level" className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-cyan-500 text-slate-900 text-[9px] font-display tracking-wider px-2 py-0.5 shadow">{card.bot ? "BOT" : `LEVEL ${card.level}`}</div>
              {!card.bot && crestFor(card.level) && (
                <img data-testid="player-card-crest" src={crestSrc(crestFor(card.level))} alt={`Level ${crestFor(card.level)} crest`} title={`Level ${crestFor(card.level)} crest`}
                  className="absolute -top-3 -right-4 w-11 h-11 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div data-testid="player-card-name" className="font-display text-lg leading-tight truncate text-amber-50">{u.display_name}</div>
                  {u.tag && <div data-testid="player-card-tag" className="font-mono text-[11px] text-slate-400">{u.tag}</div>}
                  {u.badges?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid="player-card-badges">
                      {u.badges.map((b) => <span key={b} className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-400/50 text-amber-200 text-[9px] font-display tracking-wider pl-1 pr-2 py-0.5"><img src={TROPHY_SRC} alt="" className="w-3.5 h-3.5 object-contain" />{b.toUpperCase()}</span>)}
                    </div>
                  )}
                </div>
                <span data-testid="player-card-online" data-online={card.online ? "true" : "false"} className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-display tracking-wider ${card.online ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40" : "bg-slate-700/60 text-slate-300 border border-white/10"}`}>
                  <span className={`w-2 h-2 rounded-full ${card.online ? "bg-emerald-400 shadow-[0_0_8px_#34d399]" : "bg-slate-400"}`} />{card.online ? "ONLINE" : "OFFLINE"}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-400"><Coins className="w-3.5 h-3.5 text-amber-400" /> {card.bot ? "Stack" : "Chips"}</div>
              <div data-testid="player-card-chips" className="font-mono text-2xl text-amber-100 leading-tight">{fmtChips(card.chips)}</div>
              {!card.bot && <div className="text-[10px] text-slate-400 mt-1" data-testid="player-card-friends">{card.friends} friend{card.friends === 1 ? "" : "s"}</div>}
              {!card.bot && (
                <div className="mt-1.5" data-testid="player-card-xp" title={`${card.stats.xp} XP · next level at ${card.stats.next_level_xp}`}>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-cyan-400 transition-[width]" style={{ width: `${card.stats.progress}%` }} /></div>
                  <div className="text-[9px] text-slate-500 mt-0.5">{card.stats.xp - card.stats.level_xp} / {card.stats.next_level_xp - card.stats.level_xp} XP to level {card.stats.level + 1}</div>
                </div>
              )}
            </div>
          </div>
          {card.location && (
            <div data-testid="player-card-location" className="flex items-center justify-between gap-3 rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-xs">
              <div className="min-w-0"><span className="text-slate-400">{isMe ? "You're" : "Currently"} playing in </span><span className="text-amber-100 truncate">{card.location.name}</span><span className="text-slate-500"> · {card.location.stakes}{card.location.format === "SNG" ? " · Sit N Go" : ""}</span></div>
              {card.location.table_id !== tableId && (
                <button data-testid="player-card-visit" onClick={() => nav(`/table/${card.location.table_id}`)} title={card.can_join ? "Visit — you can take a seat there" : "Visit — Sit N Go in progress, spectate only"}
                  className="btn-gold rounded-full px-3 py-1 text-[11px] font-display tracking-wider flex items-center gap-1 shrink-0 btn-lift"><ExternalLink className="w-3 h-3" /> VISIT</button>
              )}
            </div>
          )}
          {!isMe && !card.bot && <div className="flex items-center justify-between"><FriendButton card={card} setCard={setCard} /></div>}
          {!card.bot && (
            <>
              {isMe ? (
                <div data-testid="player-card-tab-stats" className="rounded-full bg-black/40 p-1 border border-white/10 text-center text-[11px] font-display tracking-wider text-amber-200/90 py-1.5 cursor-default select-none">STATS</div>
              ) : (
              <div className="flex gap-1 rounded-full bg-black/40 p-1 border border-white/10" role="tablist">
                {[["stats", "STATS"], ["notes", "NOTES"]].map(([id, label]) => (
                  <button key={id} role="tab" aria-selected={tab === id} data-testid={`player-card-tab-${id}`} onClick={() => setTab(id)}
                    className={`flex-1 rounded-full py-1 text-[11px] font-display tracking-wider transition-colors ${tab === id ? "bg-amber-500 text-slate-900 cursor-default" : "text-slate-300 hover:bg-white/10"}`}>{label}</button>
                ))}
              </div>
              )}
              {tab === "stats" ? <StatsTab s={card.stats} /> : <NotesTab userId={u.user_id} note={card.note} onSaved={(n) => { setCard((c) => ({ ...c, note: n })); onNoteSaved?.(u.user_id, n); }} />}
            </>
          )}
          {card.bot && <div className="text-xs text-slate-400">Practice bot — no profile or stats.</div>}
        </div>
      )}
    </aside>
  );
};
